import type {Template} from 'src/types/scheduler/templates';
import type {SchedulerTemplateForm} from 'src/types/scheduler/template-form';
import {AnalyticsService} from 'src/analytics';
import {
  SCHEDULER_AMPLITUDE_EVENTS,
  SCHEDULER_TEAM_PERFORMANCE_ANALYTICS_PAGE_NAME,
  type SchedulerMeetingsDrilldownRouteType,
} from './scheduler-amplitude-constants';

import {
  buildTemplateCreatedAmplitudeProps,
  getSchedulerAmplitudeTemplateTypeLabel,
  getTemplateUpdatedFieldsChanged,
  getMeetingsDrilldownExportTableContext,
  getSchedulerDashboardUserRoleLabel,
  templatesPageLabel,
} from './scheduler-amplitude-helper';
import {getTemplateFormAnalyticsState} from './template-form-analytics-state';


export function trackDashboardPageVisited({
  agency_id,
  agency_name,
  isAdmin,
}: {
  agency_id: string;
  agency_name: string;
  isAdmin: boolean;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DASHBOARD.DASHBOARD_PAGE_VISITED,
    {
      agency_id,
      agency_name,
      page_name: SCHEDULER_TEAM_PERFORMANCE_ANALYTICS_PAGE_NAME,
      user_role: getSchedulerDashboardUserRoleLabel(isAdmin),
    },
  );
}

export function trackDashboardStatCardClicked({
  agency_id,
  agency_name,
  cardType,
  cardValue,
  cardDuration,
}: {
  agency_id: string;
  agency_name: string;
  cardType: string;
  cardValue: number;
  cardDuration: number;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DASHBOARD.STAT_CARD_CLICKED,
    {
      agency_id,
      agency_name,
      card_type: cardType,
      card_value: cardValue,
      card_duration: cardDuration,
    },
  );
}

export function trackMeetingsDrilldownExportClicked({
  agency_id,
  agency_name,
  drilldownType,
  isFromTeamMemberDrilldown,
  rowCount,
}: {
  agency_id: string;
  agency_name: string;
  drilldownType: SchedulerMeetingsDrilldownRouteType;
  isFromTeamMemberDrilldown: boolean;
  rowCount: number;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DASHBOARD.MEETINGS_TABLE_EXPORT_CLICKED,
    {
      agency_id,
      agency_name,
      table_context: getMeetingsDrilldownExportTableContext(
        drilldownType,
        isFromTeamMemberDrilldown,
      ),
      row_count: rowCount,
    },
  );
}

export function trackTeamMemberRowClicked({
  agency_id,
  agency_name,
  memberName,
  memberId,
}: {
  agency_id: string;
  agency_name: string;
  memberName: string;
  memberId: string;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DASHBOARD.TEAM_MEMBER_ROW_CLICKED,
    {
      agency_id,
      agency_name,
      member_name: memberName,
      member_id: memberId,
    },
  );
}

export function trackMemberProfileViewed({
  agency_id,
  agency_name,
  memberName,
  memberId,
  meetingsThisWeek,
  totalHoursWeek,
  availableHours,
}: {
  agency_id: string;
  agency_name: string;
  memberName: string;
  memberId: string;
  meetingsThisWeek: number;
  totalHoursWeek: number;
  availableHours: number;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DASHBOARD.MEMBER_PROFILE_VIEWED,
    {
      agency_id,
      agency_name,
      member_name: memberName,
      member_id: memberId,
      meetings_this_week: meetingsThisWeek,
      total_hours_week: totalHoursWeek,
      available_hours: availableHours,
    },
  );
}

export function trackMeetingAssignmentConfirmed({
  agency_id,
  agency_name,
  meetingsAssigned,
  reason,
  originalMember,
  newHost,
}: {
  agency_id: string;
  agency_name: string;
  meetingsAssigned: number;
  reason: string;
  originalMember: string;
  newHost: string;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DASHBOARD.MEETING_ASSIGNMENT_CONFIRMED,
    {
      agency_id,
      agency_name,
      meetings_assigned: meetingsAssigned,
      reason,
      original_member: originalMember,
      new_host: newHost,
    },
  );
}

export function trackMeetingRescheduleRequested({
  agency_id,
  agency_name,
  meetingsCount,
  reason,
  memberName,
}: {
  agency_id: string;
  agency_name: string;
  meetingsCount: number;
  reason: string;
  memberName: string;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DASHBOARD.MEETING_RESCHEDULE_REQUESTED,
    {
      agency_id,
      agency_name,
      meetings_count: meetingsCount,
      reason,
      member_name: memberName,
    },
  );
}

export function trackMeetingCancellationConfirmed({
  agency_id,
  agency_name,
  meetingsCount,
  reason,
  memberName,
}: {
  agency_id: string;
  agency_name: string;
  meetingsCount: number;
  reason: string;
  memberName: string;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DASHBOARD.MEETING_CANCELLATION_CONFIRMED,
    {
      agency_id,
      agency_name,
      meetings_count: meetingsCount,
      reason,
      member_name: memberName,
    },
  );
}

export function trackAssignMeetingsPanelOpened({
  agency_id,
  agency_name,
  memberName,
  memberId,
}: {
  agency_id: string;
  agency_name: string;
  memberName: string;
  memberId: string;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DASHBOARD.ASSIGN_MEETINGS_PANEL_OPENED,
    {
      agency_id,
      agency_name,
      member_name: memberName,
      member_id: memberId,
    },
  );
}

export function trackRescheduleCancelOptionSelected({
  agency_id,
  agency_name,
  memberName,
  memberId,
}: {
  agency_id: string;
  agency_name: string;
  memberName: string;
  memberId: string;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DASHBOARD.RESCHEDULE_CANCEL_OPTION_SELECTED,
    {
      agency_id,
      agency_name,
      member_name: memberName,
      member_id: memberId,
    },
  );
}

export function trackUpdateScheduleDropdownOpened({
  agency_id,
  agency_name,
  memberName,
  memberId,
}: {
  agency_id: string;
  agency_name: string;
  memberName: string;
  memberId: string;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DASHBOARD.UPDATE_SCHEDULE_DROPDOWN_OPENED,
    {
      agency_id,
      agency_name,
      member_name: memberName,
      member_id: memberId,
    },
  );
}

export function trackAssignMeetingHostSelected({
  agency_id,
  agency_name,
  meetingTitle,
  meetingId,
  selectedHost,
  hostRank,
}: {
  agency_id: string;
  agency_name: string;
  meetingTitle: string;
  meetingId: string;
  selectedHost: string;
  hostRank: number;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DASHBOARD.ASSIGN_MEETING_HOST_SELECTED,
    {
      agency_id,
      agency_name,
      meeting_title: meetingTitle,
      meeting_id: meetingId,
      selected_host: selectedHost,
      host_rank: hostRank,
    },
  );
}

export function trackIndividualTemplatesPageVisited(
  agency_id: string,
  agency_name: string,
): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.TEMPLATE.TEMPLATES_PAGE_VISITED,
    {agency_id, agency_name},
  );
}

export function trackTeamTemplatesPageVisited(
  agency_id: string,
  agency_name: string,
): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.TEMPLATE.TEAM_TEMPLATES_PAGE_VISITED,
    {agency_id, agency_name},
  );
}

export function trackTemplateCreationCancelled(
  agency_id: string,
  agency_name: string,
  isTeamTemplate: boolean,
): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.TEMPLATE.TEMPLATE_CREATION_CANCELLED,
    {
      agency_id,
      agency_name,
      page: templatesPageLabel(isTeamTemplate),
    },
  );
}

export function trackCopyLinkClicked({
  agency_id,
  agency_name,
  isTeamTemplate,
  urlType,
  distributionRule,
}: {
  agency_id: string;
  agency_name: string;
  isTeamTemplate: boolean;
  urlType: string;
  distributionRule: string;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.TEMPLATE.COPY_LINK_CLICKED,
    {
      agency_id,
      agency_name,
      template_type: getSchedulerAmplitudeTemplateTypeLabel(distributionRule),
      page: templatesPageLabel(isTeamTemplate),
      url_type: urlType,
    },
  );
}

export function trackTemplateCreated({
  agency_id,
  agency_name,
  template,
  isTeamTemplate,
}: {
  agency_id: string;
  agency_name: string;
  template: Template;
  isTeamTemplate: boolean;
}): void {
  AnalyticsService.track(SCHEDULER_AMPLITUDE_EVENTS.TEMPLATE.TEMPLATE_CREATED, {
    agency_id,
    agency_name,
    page: templatesPageLabel(isTeamTemplate),
    ...buildTemplateCreatedAmplitudeProps(template as SchedulerTemplateForm),
  });
}

export function trackTemplateDeleted({
  agency_id,
  agency_name,
  distributionRule,
  pageIsTeamTemplate,
}: {
  agency_id: string;
  agency_name: string;
  distributionRule: string;
  pageIsTeamTemplate: boolean;
}): void {
  AnalyticsService.track(SCHEDULER_AMPLITUDE_EVENTS.TEMPLATE.TEMPLATE_DELETED, {
    agency_id,
    agency_name,
    template_type: getSchedulerAmplitudeTemplateTypeLabel(distributionRule),
    page: templatesPageLabel(pageIsTeamTemplate),
  });
}

export function trackTemplateSaveSuccess({
  templateFormBeforeOverwrite,
  agency,
}: {
  templateFormBeforeOverwrite: SchedulerTemplateForm;
  agency: {id: string; name: string};
}): void {
  const baseline = getTemplateFormAnalyticsState();
  if (baseline == null) {
    return;
  }
  const fieldsChanged = getTemplateUpdatedFieldsChanged(
    baseline,
    templateFormBeforeOverwrite,
  );
  if (fieldsChanged === '') {
    return;
  }

  AnalyticsService.track(SCHEDULER_AMPLITUDE_EVENTS.TEMPLATE.TEMPLATE_UPDATED, {
    agency_id: agency.id,
    agency_name: agency.name,
    template_type: getSchedulerAmplitudeTemplateTypeLabel(
      templateFormBeforeOverwrite.distributionRule,
    ),
    fields_changed: fieldsChanged,
  });
}
